import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { ProjectState } from '@models/project';
import {
  convertUserSettingsProjectStateToProjectState,
  UserSettings,
  UserSettingsProjectState,
  UserSettingsSettings,
} from '@models/user-settings';
import { useUserSettings } from '@providers';
import { useMemo } from 'react';
import {
  QueryResult,
  useFetch,
  UseFetchResult,
  useUpdateMutation,
} from '../common';

export function useGetUserSettings(): UseFetchResult<UserSettings> {
  const { accounts, inProgress } = useMsal();
  const isAuthenticationReady =
    inProgress === InteractionStatus.None && accounts.length > 0;

  const result = useFetch<QueryResult<UserSettings>>('benutzerEinstellung', {
    enabled: isAuthenticationReady,
  });

  const wrappedRefetch = async (): Promise<UserSettings | null> => {
    const queryResult = await result.refetch();
    return queryResult?.data ?? null;
  };

  return {
    ...result,
    result: result.result?.data ?? null,
    refetch: wrappedRefetch,
  };
}

export function useSetOrganization() {
  const userSetting = useUserSettings();

  const result = useUpdateMutation<UserSettings>(
    'benutzerEinstellung/Organisation/{id}',
    { showSuccessNotification: false },
  );
  return {
    ...result,
    mutate: async (
      data?: unknown,
      endpointFormatObj?: Record<string, string>,
    ) => {
      const userSettingResult = await result.mutate(data, endpointFormatObj);
      if (userSettingResult?.data) {
        userSetting.set(userSettingResult.data);
      }
      return userSettingResult;
    },
  };
}

export function useGetPinnedProjectStates(
  organisationId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? !!organisationId;

  const result = useFetch<QueryResult<ProjectState[]>>(
    organisationId
      ? `BenutzerEinstellung/Projectstate/Pin/${organisationId}`
      : '',
    { method: 'GET', enabled },
  );

  return useMemo(
    () => ({ ...result, result: result.result?.data ?? [] }),
    [result],
  );
}

export function useGetRecentProjectStates(
  organisationId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? !!organisationId;

  const result = useFetch<QueryResult<ProjectState[]>>(
    organisationId
      ? `BenutzerEinstellung/Projectstate/Recent/${organisationId}`
      : '',
    { method: 'GET', enabled },
  );

  return useMemo(
    () => ({ ...result, result: result.result?.data ?? [] }),
    [result],
  );
}

export function useSetPinProjectState() {
  return useUpdateMutation<void>(
    'BenutzerEinstellung/Projectstate/Pin/{projektstadiumId}/{organisationId}/{istAngeheftet}',
    { showSuccessNotification: false, method: 'POST' },
  );
}

function useSetRecentProjectState() {
  return useUpdateMutation<void>(
    'BenutzerEinstellung/Projectstate/Recent/{projektstadiumId}/{organisationId}/{istZuletztVerwendet}',
    { showSuccessNotification: false, method: 'POST' },
  );
}

export function useSetCurrentProjectState() {
  const userSettings = useUserSettings();
  const setRecentProjectState = useSetRecentProjectState();

  const result = useUpdateMutation<ProjectState>(
    'benutzerEinstellung/OrganisationEinstellung',
    {
      showSuccessNotification: true,
      method: 'PUT',
      successMessage: 'Projekt wurde erfolgreich gewählt.',
    },
  );

  return {
    ...result,
    mutate: async (projectState: UserSettingsProjectState) => {
      const organisationId =
        userSettings.value.aktuelleOrganisation?.organisationId;

      if (projectState.projektstadiumId) {
        await setRecentProjectState.mutate(undefined, {
          projektstadiumId: projectState.projektstadiumId,
          organisationId,
          istZuletztVerwendet: 'true',
        });
      }

      const convertedProjectState =
        convertUserSettingsProjectStateToProjectState(
          projectState,
        ) as ProjectState;
      const userSettingsSetting: UserSettingsSettings = {
        benutzerId: userSettings.value.benutzerId,
        organisationId,
        aktuellesProjektstadium: convertedProjectState,
      };

      await result.mutate(userSettingsSetting);
      userSettings.set({
        ...userSettings.value,
        einstellungen: {
          ...userSettings.value.einstellungen,
          aktuellesProjektstadium: {
            ...userSettings.value.einstellungen?.aktuellesProjektstadium,
            ...convertedProjectState,
          },
        },
      });
    },
  };
}
